/**
 * Starter StatCard — the dashboard at-a-glance metric tile.
 * Composition: eyebrow label, big display number, optional trend chip,
 * one-line meta. Matches the design's web-app `.stat` block.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { FitText } from './FitText';
import styles from './StatCard.module.css';

export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Uppercase eyebrow label (`MORNING BRIEFING`, `ACTIVE PROJECTS`). */
  readonly label: string;
  /** The headline value. Pass a string (`'47'`, `'94.7%'`) or any ReactNode. */
  readonly value: ReactNode;
  /** Optional trend chip on the right (e.g. `▲ 12%`). Use `trendDirection` for colour. */
  readonly trend?: ReactNode;
  /** Drives the colour of `trend`. Defaults to `up` if a `trend` is given. */
  readonly trendDirection?: 'up' | 'down' | 'flat';
  /** Single-line meta text below the value (`vs last week`, `2 high, 8 medium`). */
  readonly meta?: ReactNode;
  /** Lift on hover (use when the card is a link to a detailed view). */
  readonly interactive?: boolean;
  /**
   * Auto-shrink the value so a long figure (a currency balance with many digits)
   * always fits its box instead of breaking out. Off by default; turn it on for
   * ledger figures whose width is unbounded (balances, NPV).
   */
  readonly fit?: boolean;
}

const TREND_CLASS: Record<NonNullable<StatCardProps['trendDirection']>, string> = {
  up: 'trendUp',
  down: 'trendDown',
  flat: 'trendFlat',
};

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  function StatCard(
    {
      label,
      value,
      trend,
      trendDirection = 'up',
      meta,
      interactive = false,
      fit = false,
      className,
      ...rest
    },
    ref,
  ) {
    const trendClassKey = TREND_CLASS[trendDirection];
    const trendClass = (styles as Record<string, string | undefined>)[trendClassKey];
    const classes = [styles.root, interactive ? styles.interactive : undefined, className]
      .filter(Boolean)
      .join(' ');
    // Make an interactive card keyboard-focusable so the focus-visible ring is
    // reachable — but never override a tabIndex the consumer supplied themselves.
    const tabIndex = interactive && rest.tabIndex === undefined ? 0 : rest.tabIndex;
    return (
      <div ref={ref} className={classes} {...rest} tabIndex={tabIndex}>
        <span className={styles.eyebrow}>{label}</span>
        <div className={styles.row}>
          {fit ? (
            <FitText className={styles.value}>{value}</FitText>
          ) : (
            <span className={styles.value}>{value}</span>
          )}
          {trend !== undefined ? (
            <span className={[styles.trend, trendClass].filter(Boolean).join(' ')}>
              {trend}
            </span>
          ) : null}
        </div>
        {meta !== undefined ? <span className={styles.meta}>{meta}</span> : null}
      </div>
    );
  },
);
